import {
  Resolver,
  FieldErrors,
  FieldError,
  Merge,
  FieldErrorsImpl,
  ResolverOptions,
  ResolverResult,
} from 'react-hook-form';
import {
  AutoRelaySwitchScheduleEventDeviceConfig,
  ModbusScheduleEventConfigType,
  RelaySwitchScheduleEventDeviceConfig,
  SingleRelaySwitchScheduleEventDeviceConfig,
  TasmotaPowerDeviceConfig,
} from '@thingslog/repositories';
import { isBefore, startOfToday } from 'date-fns';
import { TFunction } from 'i18next';
import { ScheduledEventForm } from '../../models/ScheduledEventDto';

export const scheduleEventCreateResolver: Resolver<ScheduledEventForm> = async (
  values: ScheduledEventForm,
  context: { t: TFunction },
  options: ResolverOptions<ScheduledEventForm>
): Promise<ResolverResult<ScheduledEventForm>> => {
  const t = context.t;
  const errors: FieldErrors<ScheduledEventForm> = {};

  const today = startOfToday();

  if (!values.eventName || values.eventName.length < 3) {
    errors.eventName = {
      type: 'minLength',
      message: t('scheduled_tasks_event_name_min_length'),
    };
  } else if (values.eventName.length > 50) {
    errors.eventName = {
      type: 'maxLength',
      message: t('scheduled_tasks_event_name_max_length'),
    };
  }

  if (!values.fromDate || isBefore(values.fromDate, today)) {
    errors.fromDate = {
      type: 'invalidDate',
      message: t('scheduled_tasks_from_date_invalid'),
    };
  }

  if (!values.toDate || isBefore(values.toDate, values.fromDate)) {
    errors.toDate = {
      type: 'invalidDate',
      message: t('scheduled_tasks_to_date_invalid'),
    };
  }

  if (values.executionHour < 0 || values.executionHour > 23) {
    errors.executionHour = {
      type: 'range',
      message: t('scheduled_tasks_execution_hour_range'),
    };
  }

  if (values.executionMinute < 0 || values.executionMinute > 59) {
    errors.executionMinute = {
      type: 'range',
      message: t('scheduled_tasks_execution_minute_range'),
    };
  }

  if (values.repeat) {
    if (!values.period || values.period <= 0 || !Number.isInteger(values.period)) {
      errors.period = {
        type: 'required',
        message: t('scheduled_tasks_period_required'),
      };
    }

    if (!values.temporal) {
      errors.temporal = {
        type: 'required',
        message: t('scheduled_tasks_temporal_required'),
      };
    }
  }

  const { event } = values;

  if (event.deviceNumbers.length <= 0) {
    errors.event = {
      ...errors.event,
      deviceNumbers: {
        type: 'required',
        message: t('scheduled_tasks_device_number_is_required'),
      },
    };
  }

  switch (event['@type']) {
    case 'SINGLE_RELAY_SWITCH': {
      const singleRelaySwitchEvent = event as SingleRelaySwitchScheduleEventDeviceConfig;

      if (
        singleRelaySwitchEvent.sensorIndex < 0 ||
        !Number.isInteger(singleRelaySwitchEvent.sensorIndex)
      ) {
        errors.event = {
          ...errors.event,
          sensorIndex: {
            type: 'range',
            message: t('scheduled_tasks_event_sensor_index_range'),
          },
        } as Merge<FieldError, FieldErrorsImpl<SingleRelaySwitchScheduleEventDeviceConfig>>;
      }
      break;
    }

    case 'RELAY_SWITCH': {
      const relaySwitchEvent = event as RelaySwitchScheduleEventDeviceConfig;

      if (!relaySwitchEvent.requiredStates || relaySwitchEvent.requiredStates.length === 0) {
        errors.event = {
          ...errors.event,
          relayStates: {
            type: 'required',
            message: t('scheduled_tasks_event_relay_states_required'),
          },
        } as Merge<FieldError, FieldErrorsImpl<RelaySwitchScheduleEventDeviceConfig>>;
      }
      break;
    }

    case 'AUTO_RELAY_SWITCH': {
      const autoRelaySwitchEvent = event as AutoRelaySwitchScheduleEventDeviceConfig;
      if (!autoRelaySwitchEvent.requiredState) {
        errors.event = {
          ...errors.event,
          requiredState: {
            type: 'required',
            message: t('scheduled_tasks_event_required_state_auto_relay'),
          },
        } as Merge<FieldError, FieldErrorsImpl<AutoRelaySwitchScheduleEventDeviceConfig>>;
      }
      break;
    }

    case 'SEND_DATE_OVER_MQTT':
      break;

    case 'MODBUS_READ_COIL':
    case 'MODBUS_WRITE_COIL':
    case 'MODBUS_READ_REGISTER': {
      const modbusEvent = event;
      if (modbusEvent.sensorIndex < 0 || !Number.isInteger(modbusEvent.sensorIndex)) {
        errors.event = {
          ...errors.event,
          sensorIndex: {
            type: 'range',
            message: t('scheduled_tasks_event_sensor_index_range'),
          },
        } as Merge<FieldError, FieldErrorsImpl<ModbusScheduleEventConfigType>>;
      }
      break;
    }

    case 'TASMOTA_POWER': {
      const tasmotaEvent = event as TasmotaPowerDeviceConfig;
      if (tasmotaEvent.sensorIndex < 0 || !Number.isInteger(tasmotaEvent.sensorIndex)) {
        errors.event = {
          ...errors.event,
          sensorIndex: {
            type: 'range',
            message: t('scheduled_tasks_event_sensor_index_range'),
          },
        } as Merge<FieldError, FieldErrorsImpl<TasmotaPowerDeviceConfig>>;
      }
      if (!tasmotaEvent.requiredState) {
        errors.event = {
          ...errors.event,
          requiredState: {
            type: 'required',
            message: t('scheduled_tasks_event_required_state_tasmota_power'),
          },
        } as Merge<FieldError, FieldErrorsImpl<TasmotaPowerDeviceConfig>>;
      }
      break;
    }

    case 'SCHEDULED_RELAY_COMMAND':
      break;

    case 'SCHEDULED_RELAY_COMMAND_4':
      break;

    default:
      errors.event = {
        ...errors.event,
        '@type': {
          type: 'invalid',
          message: t('scheduled_tasks_event_type_invalid'),
        },
      };
  }
  return {
    values: Object.keys(errors).length === 0 ? values : {},
    errors,
  };
};
